import React, { useState, useEffect, useRef} from 'react';
import axios from 'axios';
import './UserParameter.css';
import { useNavigate, useSearchParams } from 'react-router-dom';
import WhatsAppIntegration from './WhatsAppIntegration';
import ChatbotSettings from './ChatbotSettings';
import { useTranslation } from 'react-i18next';
import { FaUser, FaEnvelope, FaLock, FaKey, FaTrash, FaCog, FaInstagram, FaGoogle, FaWhatsapp, FaRobot, FaWarehouse, FaTimes, FaPlus, FaSave, FaCheck, FaCheckCircle, FaExclamationTriangle, FaCreditCard, FaEye, FaEyeSlash, FaShoppingCart, FaBan, FaSlack, FaHashtag, FaEnvelopeOpenText, FaLink, FaShieldAlt, FaExclamationCircle, FaServer, FaInfoCircle, FaTrashAlt, FaFilter, FaPlug, FaEdit } from 'react-icons/fa';
import { SiMicrosoftoutlook } from 'react-icons/si';

const TABS = {
  PROFILE: 'profile',
  INTEGRATIONS: 'integrations',
  NOTIFICATIONS: 'notifications',
  SECURITY: 'security'
};

const UserParameter = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Core state — active tab lives in the URL so refresh and the OAuth
  // callback redirects land back on the right tab.
  const tabParam = searchParams.get('tab');
  const activeTab = Object.values(TABS).includes(tabParam) ? tabParam : TABS.PROFILE;
  // The voice agent's numbers moved to their own section (Agents vocaux ›
  // Numéros, 2026-10-07): old links and Stripe returns land there.
  useEffect(() => {
    if (tabParam !== 'voice') return;
    const next = new URLSearchParams(searchParams);
    next.delete('tab');
    navigate(`/voice-numbers${next.toString() ? `?${next.toString()}` : ''}`, { replace: true });
  }, [tabParam, searchParams, navigate]);
  const setActiveTab = (tab) => {
    const next = new URLSearchParams(searchParams);
    if (tab === TABS.PROFILE) next.delete('tab'); else next.set('tab', tab);
    setSearchParams(next, { replace: true });
  };
  const [user, setUser] = useState(undefined);
  const [isExperienceUser, setIsExperienceUser] = useState(false);
  
  // Modal states
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showWhatsAppModal, setShowWhatsAppModal] = useState(false);
  const [showChatbotModal, setShowChatbotModal] = useState(false);
  
  // Password form
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  
  // Delete confirmation
  const [confirmUsername, setConfirmUsername] = useState('');
  
  // Instagram accounts selection
  const [instagramAccounts, setInstagramAccounts] = useState([]);
  
  // Loading states
  const [isWmsConnecting, setIsWmsConnecting] = useState(false);
  const [isRegisteringWebhook, setIsRegisteringWebhook] = useState(false);
  const [isRemovingWebhook, setIsRemovingWebhook] = useState(false);
  const [isRegisteringSaveOnlyWebhook, setIsRegisteringSaveOnlyWebhook] = useState(false);
  
  // Outlook accounts
  const [outlookAccounts, setOutlookAccounts] = useState([]);
  const [isLoadingOutlookAccounts, setIsLoadingOutlookAccounts] = useState(false);
  
  // Subscriptions
  const [subscriptions, setSubscriptions] = useState([]);
  const [isLoadingSubscriptions, setIsLoadingSubscriptions] = useState(false);
  const [showSubscriptions, setShowSubscriptions] = useState(false);
  
  // Slack integration
  const [slackToken, setSlackToken] = useState('');
  const [slackChannelId, setSlackChannelId] = useState('');
  const [isLoadingSlackCredentials, setIsLoadingSlackCredentials] = useState(false);
  const [isSavingSlackCredentials, setIsSavingSlackCredentials] = useState(false);
  const [showSlackToken, setShowSlackToken] = useState(false);

  // Spacefill integration (ticketing platform, per-tenant API token)
  const [spacefillEnabled, setSpacefillEnabled] = useState(true);
  const [spacefillConfigured, setSpacefillConfigured] = useState(false);
  const [spacefillTokenSet, setSpacefillTokenSet] = useState(false);
  const [spacefillTokenInput, setSpacefillTokenInput] = useState('');
  const [spacefillSyncState, setSpacefillSyncState] = useState(null);
  const [spacefillOptions, setSpacefillOptions] = useState({});
  const [spacefillIncidentTypesInput, setSpacefillIncidentTypesInput] = useState('');
  const [isLoadingSpacefill, setIsLoadingSpacefill] = useState(false);
  const [isSavingSpacefill, setIsSavingSpacefill] = useState(false);
  const [showSpacefillToken, setShowSpacefillToken] = useState(false);
  
  // Ignored senders (addresses and domains that never open a ticket)
  const [blockedEntries, setBlockedEntries] = useState([]);
  const [blockedInvalid, setBlockedInvalid] = useState([]);
  const [newBlockedEntry, setNewBlockedEntry] = useState('');
  const [blockedEntryError, setBlockedEntryError] = useState('');
  const [isLoadingBlockedEmails, setIsLoadingBlockedEmails] = useState(false);
  const [isSavingBlockedEmails, setIsSavingBlockedEmails] = useState(false);
  
  // OTP change
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [newOtpCode, setNewOtpCode] = useState('');
  const [isSubmittingOtp, setIsSubmittingOtp] = useState(false);
  const [submitOtpError, setSubmitOtpError] = useState(null);
  const [submitOtpSuccess, setSubmitOtpSuccess] = useState(null);

  // The account's Gmail mailbox connected by app password (/api/logistics/smtp-config):
  // { mailbox, candidates (carrier pages' 2FA mailboxes), outlook_connected }.
  const [smtpConfig, setSmtpConfig] = useState(null);
  const [smtpReplacing, setSmtpReplacing] = useState(false);
  const [smtpLoading, setSmtpLoading] = useState(false);
  const [smtpSaving, setSmtpSaving] = useState(false);
  const [smtpForm, setSmtpForm] = useState({
    email: '',
    password: '',
    smtp_server: 'smtp.gmail.com',
    smtp_port: 587,
  });
  const [showSmtpPassword, setShowSmtpPassword] = useState(false);




  // Popup message
  // Feedback banner. setPopupMessage keeps its historical signature (a plain
  // translated string) — the tone is inferred from the message because the 50
  // call sites predate the banner (the old popupMessage was never rendered).
  const [feedback, setFeedback] = useState(null);
  const feedbackTimerRef = useRef(null);
  const setPopupMessage = (message) => {
    if (!message) return;
    const isError = /fail|error|échec|impossible|erreur|invalid/i.test(message);
    clearTimeout(feedbackTimerRef.current);
    setFeedback({ type: isError ? 'error' : 'success', text: message });
    if (!isError) {
      feedbackTimerRef.current = setTimeout(() => setFeedback(null), 5000);
    }
  };
  useEffect(() => () => clearTimeout(feedbackTimerRef.current), []);

  // ============================================
  // INITIALIZATION
  // ============================================
  useEffect(() => {
    const token = localStorage.getItem('access_token');
    if (!token) {
      navigate('/');
      return;
    }
    
    const userDataString = localStorage.getItem('userData');
    if (userDataString) {
      try {
        const parsedUserData = JSON.parse(userDataString);
        if (parsedUserData.role === 'experience') {
          setIsExperienceUser(true);
          setUser({});
          return;
        }
      } catch (e) {
        console.error("Error parsing user data for experience check", e);
      }
    }

    fetchUserData(token);
    fetchOutlookAccounts();
    fetchSlackCredentials();
    fetchSpacefillConfig();
    fetchBlockedEmails();
    fetchSmtpConfig();
  }, [navigate]);

  useEffect(() => {
    if (user === null && !isExperienceUser) {
      navigate('/');
    }
  }, [user, navigate, isExperienceUser]);

  useEffect(() => {
    if (!isExperienceUser) {
      checkConnectionStatuses();
      const urlParams = new URLSearchParams(window.location.search);
      const code = urlParams.get('code');
      const state = urlParams.get('state');
      if (code && state) {
        handleInstagramCallback(code, state);
      }
    }
  }, [isExperienceUser]);

  // ============================================
  // DATA FETCHING
  // ============================================
  const fetchUserData = async (token) => {
    try {
      const response = await axios.get('/api/users/user', {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.data) {
        setUser({
          username: response.data.username || '',
          firstname: response.data.firstname || '',
          lastname: response.data.lastname || '',
          email: response.data.email || '',
          subscription_plan: response.data.subscription_plan || '',
          user_meta_id: response.data.user_meta_id || '',
          user_insta_id: response.data.user_insta_id || '',
          gmail_email_address: response.data.gmail_email || '',
          automation: response.data.automation || '',
          shop_name: response.data.shop_name || '',
          instagram_username: response.data.insta_username || '',
          whatsapp_phone_number: response.data.whatsapp_phone_number || '',
          edn_api_account: response.data.edn_api_account || '',
          unanimity_wms_id: response.data.unanimity_wms_id || '',
          outlook_email: response.data.outlook_email || '',
          fa_account: response.data.fa_account || false,
        });
      } else {
        setUser(null);
      }
    } catch (error) {
      console.error('Failed to fetch user data:', error);
      setUser(null);
    }
  };

  const fetchOutlookAccounts = async () => {
    if (isExperienceUser) return;
    try {
      setIsLoadingOutlookAccounts(true);
      const token = localStorage.getItem('access_token');
      const response = await axios.get('/api/social/outlook/accounts', {
        headers: { Authorization: `Bearer ${token}` },
      });
      setOutlookAccounts(response.data.accounts || []);
    } catch (error) {
      console.error('Failed to fetch Outlook accounts:', error);
    } finally {
      setIsLoadingOutlookAccounts(false);
    }
  };

  const fetchSlackCredentials = async () => {
    if (isExperienceUser) return;
    try {
      setIsLoadingSlackCredentials(true);
      const token = localStorage.getItem('access_token');
      const response = await axios.get('/api/users/user', {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSlackToken(response.data.slack_token || '');
      setSlackChannelId(response.data.slack_channel_id || '');
    } catch (error) {
      console.error('Failed to fetch Slack credentials:', error);
    } finally {
      setIsLoadingSlackCredentials(false);
    }
  };

  const fetchBlockedEmails = async () => {
    if (isExperienceUser) return;
    try {
      setIsLoadingBlockedEmails(true);
      const token = localStorage.getItem('access_token');
      const response = await axios.get('/api/users/user/blocked-emails', {
        headers: { Authorization: `Bearer ${token}` },
      });
      setBlockedEntries(response.data.entries || []);
      setBlockedInvalid(response.data.invalid || []);
    } catch (error) {
      console.error('Failed to fetch blocked emails:', error);
    } finally {
      setIsLoadingBlockedEmails(false);
    }
  };

  // ============================================
  // CONNECTION STATUS CHECKS
  // ============================================
  const checkConnectionStatuses = () => {
    if (isExperienceUser) return;
    const urlParams = new URLSearchParams(window.location.search);
    const instaStatus = urlParams.get('status');
    const googleStatus = urlParams.get('google_status');
    const shopifyStatus = urlParams.get('shopify_status');
    const outlookStatus = urlParams.get('outlook_status');

    if (instaStatus === 'success') setPopupMessage(t('userParameter.instagramConnected', 'Compte Instagram connecté.'));
    else if (instaStatus === 'failure') setPopupMessage(t('userParameter.instagramConnectError', 'Échec de la connexion du compte Instagram.'));

    if (googleStatus === 'success') setPopupMessage(t('userParameter.gmailConnected', 'Gmail connecté.'));
    else if (googleStatus === 'failure') setPopupMessage(t('userParameter.gmailConnectError', 'Échec de la connexion du compte Google.'));

    if (shopifyStatus === 'success') setPopupMessage(t('userParameter.shopifyConnected', 'Boutique Shopify connectée.'));
    else if (shopifyStatus === 'failure') setPopupMessage(t('userParameter.shopifyConnectError', 'Échec de la connexion du compte Shopify.'));

    if (outlookStatus === 'success') setPopupMessage(t('userParameter.outlookConnected', 'Outlook connecté.'));
    else if (outlookStatus === 'failure') setPopupMessage(t('userParameter.outlookConnectError', 'Échec de la connexion du compte Outlook.'));
  };

  // ============================================
  // INSTAGRAM HANDLERS
  // ============================================
  const handleConnectInstagram = async () => {
    if (isExperienceUser) return;
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get('/api/social/instagram/connect', {
        headers: { Authorization: `Bearer ${token}` },
      });
      window.location.href = response.data.authorization_url;
    } catch (error) {
      console.error('Failed to connect Instagram account:', error);
    }
  };

  const handleInstagramCallback = async (code, state) => {
    if (isExperienceUser) return;
    const data = await fetchInstagramAccounts(code, state);
    if (data) {
      setInstagramAccounts(data.accounts);
    }
  };

  const fetchInstagramAccounts = async (code, state) => {
    if (isExperienceUser) return null;
    try {
      const response = await axios.get(`/api/social/instagram/callback?code=${code}&state=${state}`);
      return response.data;
    } catch (error) {
      console.error('Failed to fetch Instagram accounts:', error);
      return null;
    }
  };

  const handleAccountSelect = async (accountId, accountName) => {
    if (isExperienceUser) return;
    try {
      const token = localStorage.getItem('access_token');
      if (!token) {
        alert('You are not logged in. Please log in again.');
        navigate('/login');
        return;
      }
      await axios.post('/api/social/instagram/select_account', {
        account_id: accountId,
        name: accountName
      }, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      navigate('/parameters?status=success');
    } catch (error) {
      if (error.response?.status === 401) {
        alert('Session expired. Please log in again.');
        navigate('/login');
      } else {
        console.error('Failed to select Instagram account:', error);
        alert('Failed to connect Instagram account.');
      }
    }
  };

  const handleDisconnectInstagram = async () => {
    if (isExperienceUser) return;
    try {
      const token = localStorage.getItem('access_token');
      await axios.delete('/api/social/instagram/unsubscribe', {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUser({ ...user, user_insta_id: '', instagram_username: '' });
      setPopupMessage('Instagram account disconnected successfully!');
    } catch (error) {
      console.error('Failed to disconnect Instagram account:', error);
      setPopupMessage('Failed to disconnect Instagram account.');
    }
  };

  // ============================================
  // GOOGLE HANDLERS
  // ============================================
  const handleConnectGoogle = async () => {
    if (isExperienceUser) return;
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get('/api/social/google/connect', {
        headers: { Authorization: `Bearer ${token}` },
      });
      window.location.href = response.data.authorization_url;
    } catch (error) {
      console.error('Failed to connect Google account:', error);
      setPopupMessage('Failed to connect Google account.');
    }
  };

  const handleDisconnectGoogle = async () => {
    if (isExperienceUser) return;
    try {
      const token = localStorage.getItem('access_token');
      await axios.delete('/api/social/google/unsubscribe', {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUser({ ...user, gmail_email_address: '' });
      setPopupMessage('Google account disconnected successfully!');
    } catch (error) {
      console.error('Failed to disconnect Google account:', error);
      setPopupMessage('Failed to disconnect Google account.');
    }
  };

  // ============================================
  // WHATSAPP HANDLERS
  // ============================================
  const handleDisconnectWhatsApp = async () => {
    if (isExperienceUser) return;
    try {
      const token = localStorage.getItem('access_token');
      await axios.delete('/api/social/disconnect/whatsappbusiness', {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUser({ ...user, whatsapp_phone_number: '' });
      setPopupMessage('WhatsApp account disconnected successfully!');
    } catch (error) {
      console.error('Failed to disconnect WhatsApp account:', error);
      setPopupMessage('Failed to disconnect WhatsApp account.');
    }
  };

  // ============================================
  // OUTLOOK HANDLERS
  // ============================================
  const handleConnectOutlook = async () => {
    if (isExperienceUser) return;
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get('/api/social/outlook/connect', {
        headers: { Authorization: `Bearer ${token}` },
      });
      window.location.href = response.data.authorization_url;
    } catch (error) {
      console.error('Failed to connect Outlook account:', error);
      setPopupMessage('Failed to connect Outlook account.');
    }
  };

  const handleDisconnectOutlook = async (outlookEmail) => {
    if (isExperienceUser) return;
    try {
      const token = localStorage.getItem('access_token');
      await axios.delete(`/api/social/outlook/unsubscribe/${outlookEmail}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPopupMessage(`Outlook account ${outlookEmail} disconnected successfully!`);
      fetchOutlookAccounts();
      fetchUserData(token);
    } catch (error) {
      console.error('Failed to disconnect Outlook account:', error);
      setPopupMessage('Failed to disconnect Outlook account.');
    }
  };

  const handleVerifySubscriptions = async () => {
    if (isExperienceUser) return;
    try {
      setIsLoadingSubscriptions(true);
      const token = localStorage.getItem('access_token');
      const response = await axios.get('/api/social/outlook/subscriptions', {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSubscriptions(response.data.accounts || []);
      setShowSubscriptions(true);
    } catch (error) {
      console.error('Failed to fetch Outlook subscriptions:', error);
      setPopupMessage('Failed to fetch Outlook subscriptions.');
    } finally {
      setIsLoadingSubscriptions(false);
    }
  };

  // ============================================
  // WMS HANDLER
  // ============================================
  const handleConnectWMS = async () => {
    if (isExperienceUser) return;
    try {
      setIsWmsConnecting(true);
      const token = localStorage.getItem('access_token');
      await axios.post('/api/users/associate-wms', null, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPopupMessage('Successfully connected to Unanimity WMS!');
      fetchUserData(token);
    } catch (error) {
      console.error('Failed to connect to WMS:', error);
      if (error.response?.status === 403) {
        setPopupMessage('Only admin and logistics users can connect to WMS.');
      } else {
        setPopupMessage('Failed to connect to Unanimity WMS.');
      }
    } finally {
      setIsWmsConnecting(false);
    }
  };

  // ============================================
  // SHOPIFY WEBHOOK HANDLERS
  // ============================================
  const handleRegisterOrderWebhook = async () => {
    if (isExperienceUser) return;
    try {
      setIsRegisteringWebhook(true);
      const token = localStorage.getItem('access_token');
      await axios.post('/api/orders/shopify_live_webhooks/register_order_webhook', null, {
        headers: { Authorization: `Bearer ${token}` },
      });
      alert('Shopify order webhook registered successfully!');
      setPopupMessage('Shopify order webhook registered successfully!');
    } catch (error) {
      console.error('Failed to register Shopify order webhook:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Unknown error occurred';
      alert(`Failed to register Shopify order webhook: ${errorMessage}`);
      setPopupMessage(`Failed to register Shopify order webhook: ${errorMessage}`);
    } finally {
      setIsRegisteringWebhook(false);
    }
  };

  const handleRemoveOrderWebhook = async () => {
    if (isExperienceUser) return;
    try {
      setIsRemovingWebhook(true);
      const token = localStorage.getItem('access_token');
      const response = await axios.delete('/api/orders/shopify_webhooks/remove_order_shopify_webhooks', {
        headers: { Authorization: `Bearer ${token}` },
      });
      const message = response.data.message || 'Shopify order webhooks removed successfully!';
      alert(message);
      setPopupMessage(message);
    } catch (error) {
      console.error('Failed to remove Shopify order webhooks:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Unknown error occurred';
      alert(`Failed to remove Shopify order webhooks: ${errorMessage}`);
      setPopupMessage(`Failed to remove Shopify order webhooks: ${errorMessage}`);
    } finally {
      setIsRemovingWebhook(false);
    }
  };

  const handleRegisterSaveOnlyWebhook = async () => {
    if (isExperienceUser) return;
    try {
      setIsRegisteringSaveOnlyWebhook(true);
      const token = localStorage.getItem('access_token');
      await axios.post('/api/orders/shopify_webhooks/register_order_shopify_webhooks', null, {
        headers: { Authorization: `Bearer ${token}` },
      });
      alert('Shopify save-only order webhook registered successfully!');
      setPopupMessage('Shopify save-only order webhook registered successfully!');
    } catch (error) {
      console.error('Failed to register Shopify save-only order webhook:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Unknown error occurred';
      alert(`Failed to register Shopify save-only order webhook: ${errorMessage}`);
      setPopupMessage(`Failed to register Shopify save-only order webhook: ${errorMessage}`);
    } finally {
      setIsRegisteringSaveOnlyWebhook(false);
    }
  };

  // ============================================
  // PASSWORD HANDLER
  // ============================================
  const handlePasswordSubmit = async (e) => {
    if (isExperienceUser) return;
    e.preventDefault();
    if (newPassword !== confirmNewPassword) {
      alert('New passwords do not match.');
      return;
    }
    try {
      const token = localStorage.getItem('access_token');
      await axios.put('/api/users/update-user', {
        currentPassword,
        password: newPassword,
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCurrentPassword('');
      setNewPassword('');
      setConfirmNewPassword('');
      alert('Password updated successfully.');
      setShowPasswordModal(false);
    } catch (error) {
      console.error('Failed to update password:', error);
      alert('Error updating password.');
    }
  };

  // ============================================
  // DELETE ACCOUNT HANDLER
  // ============================================
  const handleDeleteUser = async (e) => {
    if (isExperienceUser) return;
    e.preventDefault();
    if (confirmUsername !== user.username) {
      alert('Username does not match.');
      return;
    }
    try {
      const token = localStorage.getItem('access_token');
      await axios.delete('/api/users/user/delete', {
        headers: { Authorization: `Bearer ${token}` },
      });
      localStorage.removeItem('access_token');
      navigate('/');
    } catch (error) {
      console.error('Failed to delete user account:', error);
      setPopupMessage(t('userParameter.deleteAccountError', 'Échec de la suppression du compte.'));
    }
  };

  // ============================================
  // SLACK CREDENTIALS HANDLER
  // ============================================
  const handleSaveSlackCredentials = async () => {
    if (isExperienceUser) return;
    try {
      setIsSavingSlackCredentials(true);
      const token = localStorage.getItem('access_token');
      await axios.put('/api/users/user/slack-credentials', {
        slack_token: slackToken || null,
        slack_channel_id: slackChannelId || null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPopupMessage(t('userParameter.slackCredentialsSaved', 'Slack credentials updated successfully!'));
    } catch (error) {
      console.error('Failed to update Slack credentials:', error);
      setPopupMessage(t('userParameter.slackCredentialsError', 'Failed to update Slack credentials.'));
    } finally {
      setIsSavingSlackCredentials(false);
    }
  };

  // ============================================
  // SPACEFILL INTEGRATION HANDLERS
  // ============================================
  const fetchSpacefillConfig = async () => {
    if (isExperienceUser) return;
    try {
      setIsLoadingSpacefill(true);
      const token = localStorage.getItem('access_token');
      const response = await axios.get('/api/integrations/config', {
        headers: { Authorization: `Bearer ${token}` },
      });
      const config = (response.data?.configs || []).find(
        (c) => c.integration_key === 'spacefill'
      );
      if (config) {
        setSpacefillConfigured(true);
        setSpacefillEnabled(config.enabled !== false);
        setSpacefillTokenSet(!!config.auth?.token);
        setSpacefillSyncState(config.sync_state || null);
        const options = config.options || {};
        setSpacefillOptions(options);
        setSpacefillIncidentTypesInput(
          Array.isArray(options.incident_types) ? options.incident_types.join(', ') : ''
        );
      }
    } catch (error) {
      console.error('Failed to fetch Spacefill configuration:', error);
    } finally {
      setIsLoadingSpacefill(false);
    }
  };

  const handleSaveSpacefillConfig = async () => {
    if (isExperienceUser) return;
    const newToken = spacefillTokenInput.trim();
    if (!spacefillTokenSet && !newToken) {
      setPopupMessage(t('userParameter.spacefillTokenRequired', 'Please enter your Spacefill API token.'));
      return;
    }
    try {
      setIsSavingSpacefill(true);
      const token = localStorage.getItem('access_token');
      const payload = { enabled: spacefillEnabled };
      // Only send auth when a new token was typed; the backend keeps the
      // stored token otherwise.
      if (newToken) {
        payload.auth = { type: 'bearer', token: newToken };
      }
      // options replaces the stored object wholesale: merge over the fetched
      // options so other knobs (initial_lookback_days...) are preserved.
      const incidentTypes = spacefillIncidentTypesInput
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);
      const options = { ...spacefillOptions };
      if (incidentTypes.length > 0) {
        options.incident_types = incidentTypes;
      } else {
        delete options.incident_types;
      }
      payload.options = options;
      await axios.put('/api/integrations/config/spacefill', payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSpacefillConfigured(true);
      setSpacefillOptions(options);
      if (newToken) {
        setSpacefillTokenSet(true);
        setSpacefillTokenInput('');
      }
      setPopupMessage(t('userParameter.spacefillSaved', 'Spacefill configuration saved! Incidents will sync within 5 minutes.'));
    } catch (error) {
      console.error('Failed to save Spacefill configuration:', error);
      setPopupMessage(t('userParameter.spacefillError', 'Failed to save Spacefill configuration.'));
    } finally {
      setIsSavingSpacefill(false);
    }
  };

  // ============================================
  // BLOCKED EMAILS HANDLER
  // ============================================
  const BLOCKED_EMAIL_RE = /^[a-z0-9._%+'-]+@[a-z0-9.-]+\.[a-z]{2,}$/i;
  const BLOCKED_DOMAIN_RE = /^(?!-)[a-z0-9-]+(\.[a-z0-9-]+)+$/i;
  const classifyBlockedEntry = (raw) => {
    const value = String(raw || '').trim().toLowerCase().replace(/^@/, '');
    if (!value) return null;
    if (BLOCKED_EMAIL_RE.test(value)) return { value, kind: 'email' };
    if (BLOCKED_DOMAIN_RE.test(value)) return { value, kind: 'domain' };
    return null;
  };

  const saveBlockedEntries = async (entries) => {
    if (isExperienceUser) return;
    try {
      setIsSavingBlockedEmails(true);
      const token = localStorage.getItem('access_token');
      const response = await axios.put('/api/users/user/blocked-emails', {
        entries: entries.map((entry) => entry.value),
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBlockedEntries(response.data.entries || entries);
      setBlockedInvalid(response.data.invalid || []);
      setPopupMessage(t('userParameter.blockedEmailsSaved', 'Ignored senders updated.'));
    } catch (error) {
      console.error('Failed to update ignored senders:', error);
      const detail = error.response?.data?.detail;
      const invalid = detail && typeof detail === 'object' && Array.isArray(detail.invalid) ? detail.invalid.join(', ') : '';
      setPopupMessage(
        invalid
          ? t('userParameter.blockedSendersInvalid', 'Not an e-mail address or a domain: {{entries}}', { entries: invalid })
          : t('userParameter.blockedEmailsError', 'Failed to update ignored senders.')
      );
    } finally {
      setIsSavingBlockedEmails(false);
    }
  };

  const handleAddBlockedEntry = async () => {
    const entry = classifyBlockedEntry(newBlockedEntry);
    if (!entry) {
      setBlockedEntryError(t('userParameter.blockedSendersInvalidOne', 'Enter an e-mail address (name@example.com) or a domain (example.com).'));
      return;
    }
    setBlockedEntryError('');
    if (blockedEntries.some((existing) => existing.value === entry.value)) {
      setNewBlockedEntry('');
      return;
    }
    const next = [...blockedEntries, entry];
    setNewBlockedEntry('');
    await saveBlockedEntries(next);
  };

  const handleRemoveBlockedEntry = async (value) => {
    await saveBlockedEntries(blockedEntries.filter((entry) => entry.value !== value));
  };

  // ============================================
  // SMTP CONFIG HANDLERS
  // ============================================
  const smtpErrorText = (error, fallback) => {
    const detail = error?.response?.data?.detail;
    return typeof detail === 'string' && detail ? detail : fallback;
  };

  const fetchSmtpConfig = async () => {
    if (isExperienceUser) return;
    try {
      setSmtpLoading(true);
      const token = localStorage.getItem('access_token');
      const response = await axios.get('/api/logistics/smtp-config', {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSmtpConfig(response.data || null);
      const mailbox = response.data?.mailbox;
      setSmtpForm({
        email: mailbox?.email || '',
        password: '',
        smtp_server: mailbox?.smtp_server || 'smtp.gmail.com',
        smtp_port: mailbox?.smtp_port || 587,
      });
    } catch {
      setSmtpConfig(null);
    } finally {
      setSmtpLoading(false);
    }
  };

  const handleSaveSmtpConfig = async () => {
    if (isExperienceUser) return;
    try {
      setSmtpSaving(true);
      const token = localStorage.getItem('access_token');
      const response = await axios.put('/api/logistics/smtp-config', {
        email: smtpForm.email,
        password: smtpForm.password,
        smtp_server: smtpForm.smtp_server,
        smtp_port: Number(smtpForm.smtp_port),
      }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSmtpConfig(response.data);
      setSmtpReplacing(false);
      setSmtpForm((f) => ({ ...f, password: '' }));
      setPopupMessage(t('accountMailbox.connected', 'Boîte Gmail du compte connectée.'));
    } catch (error) {
      console.error('Failed to connect the account mailbox:', error?.response?.status);
      setPopupMessage(smtpErrorText(error, t('accountMailbox.connectError', 'Échec de la connexion de la boîte.')));
    } finally {
      setSmtpSaving(false);
    }
  };

  const handleUseOtpMailbox = async (carrierId) => {
    if (isExperienceUser) return;
    try {
      setSmtpSaving(true);
      const token = localStorage.getItem('access_token');
      const response = await axios.post('/api/logistics/smtp-config/from-otp-mailbox', { carrier_id: carrierId }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSmtpConfig(response.data);
      setSmtpReplacing(false);
      setPopupMessage(t('accountMailbox.connected', 'Boîte Gmail du compte connectée.'));
    } catch (error) {
      console.error('Failed to connect the account mailbox from the 2FA mailbox:', error?.response?.status);
      setPopupMessage(smtpErrorText(error, t('accountMailbox.connectError', 'Échec de la connexion de la boîte.')));
    } finally {
      setSmtpSaving(false);
    }
  };

  const handleRemoveSmtpConfig = async () => {
    if (isExperienceUser) return;
    if (!window.confirm(t('accountMailbox.disconnectConfirm',
      'Déconnecter la boîte Gmail du compte ? Les nouveaux e-mails transporteur partiront de la boîte de la plateforme, et les fils déjà ouverts depuis cette boîte ne pourront plus être poursuivis.'))) return;
    try {
      setSmtpSaving(true);
      const token = localStorage.getItem('access_token');
      const response = await axios.delete('/api/logistics/smtp-config', {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSmtpConfig(response.data);
      setSmtpForm({ email: '', password: '', smtp_server: 'smtp.gmail.com', smtp_port: 587 });
      setPopupMessage(t('accountMailbox.disconnected', 'Boîte Gmail du compte déconnectée.'));
    } catch (error) {
      console.error('Failed to disconnect the account mailbox:', error?.response?.status);
      setPopupMessage(smtpErrorText(error, t('accountMailbox.disconnectError', 'Échec de la déconnexion de la boîte.')));
    } finally {
      setSmtpSaving(false);
    }
  };

  // ============================================
  // OTP CHANGE HANDLERS
  // ============================================
  const handleOpenOtpModal = () => {
    setShowOtpModal(true);
    setSubmitOtpError(null);
    setSubmitOtpSuccess(null);
    setNewOtpCode('');
  };

  const handleCloseOtpModal = () => {
    setShowOtpModal(false);
    setNewOtpCode('');
    setSubmitOtpError(null);
    setSubmitOtpSuccess(null);
  };

  const handleSubmitOtpChange = async (e) => {
    e.preventDefault();
    setIsSubmittingOtp(true);
    setSubmitOtpError(null);
    setSubmitOtpSuccess(null);

    const token = localStorage.getItem('access_token');

    if (!newOtpCode.trim()) {
      setSubmitOtpError(t('otpCodeRequired', 'OTP code is required.'));
      setIsSubmittingOtp(false);
      return;
    }

    try {
      await axios.put('/api/users/user/otp',
        { otp_code: newOtpCode },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setSubmitOtpSuccess(t('otpUpdatedSuccessfully', 'OTP code updated successfully!'));
      setNewOtpCode('');
      setTimeout(() => {
        handleCloseOtpModal();
      }, 2500);
    } catch (error) {
      console.error('Failed to update OTP code:', error);
      setSubmitOtpError(
        error.response?.data?.detail ||
        t('failedToUpdateOtp', 'Failed to update OTP code. Please try again.')
      );
    } finally {
      setIsSubmittingOtp(false);
    }
  };

  // ============================================
  // EXPERIENCE USER VIEW
  // ============================================
  if (isExperienceUser) {
    return (
      <div className="settings-page">
        <div className="experience-container">
          <div className="experience-card">
            <div className="experience-icon">
              <FaBan />
            </div>
            <h2>{t('userParameter.experience.title', 'Settings Not Available')}</h2>
            <p>{t('userParameter.experience.message', 'The user parameters page is currently not available for experience accounts.')}</p>
            <button onClick={() => navigate('/dashboard')} className="btn btn-primary">
              {t('userParameter.experience.backToDashboard', 'Back to Dashboard')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ============================================
  // LOADING STATE
  // ============================================
  if (user === undefined) {
    return (
      <div className="settings-page">
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <span>{t('userParameter.loading', 'Loading settings...')}</span>
        </div>
      </div>
    );
  }

  // ============================================
  // ERROR STATE
  // ============================================
  if (!user) {
    return (
      <div className="settings-page">
        <div className="error-state">
          <FaExclamationCircle />
          <span>{t('userParameter.loadError', 'Failed to load user data')}</span>
        </div>
      </div>
    );
  }

  // ============================================
  // MAIN RENDER
  // ============================================
  return (
    <div className="settings-page">
      <div className="settings-layout">
        {/* Header */}
        <header className="settings-header">
          <h1>{t('userParameter.title', 'Settings')}</h1>
          <p>{t('userParameter.subtitle', 'Manage your account, integrations, and preferences')}</p>
        </header>

        {/* Tab Navigation */}
        <nav className="settings-tabs">
          <button
            className={`settings-tab ${activeTab === TABS.PROFILE ? 'active' : ''}`}
            onClick={() => setActiveTab(TABS.PROFILE)}
          >
            <FaUser /> <span>{t('userParameter.tabs.profile', 'Profile')}</span>
          </button>
          <button
            className={`settings-tab ${activeTab === TABS.INTEGRATIONS ? 'active' : ''}`}
            onClick={() => setActiveTab(TABS.INTEGRATIONS)}
          >
            <FaLink /> <span>{t('userParameter.tabs.integrations', 'Integrations')}</span>
          </button>
          <button
            className={`settings-tab ${activeTab === TABS.NOTIFICATIONS ? 'active' : ''}`}
            onClick={() => setActiveTab(TABS.NOTIFICATIONS)}
          >
            <FaEnvelope /> <span>{t('userParameter.tabs.notifications', 'Notifications')}</span>
          </button>
          <button
            className={`settings-tab ${activeTab === TABS.SECURITY ? 'active' : ''}`}
            onClick={() => setActiveTab(TABS.SECURITY)}
          >
            <FaShieldAlt /> <span>{t('userParameter.tabs.security', 'Security')}</span>
          </button>
        </nav>

        {feedback && (
          <div className={`settings-feedback ${feedback.type}`} role="status">
            {feedback.type === 'success' ? <FaCheckCircle /> : <FaExclamationCircle />}
            <span>{feedback.text}</span>
            <button type="button" onClick={() => setFeedback(null)} aria-label={t('common.cancel', 'Fermer')}>
              <FaTimes />
            </button>
          </div>
        )}

        {/* Profile Panel */}
        <div className={`settings-panel ${activeTab === TABS.PROFILE ? 'active' : ''}`}>
          {/* Account Information */}
          <div className="settings-card">
            <div className="card-header">
              <div className="card-title-group">
                <h3 className="card-title"><FaUser /> {t('userParameter.accountInfo', 'Account Information')}</h3>
                <p className="card-description">{t('userParameter.accountInfoDesc', 'Your personal account details')}</p>
              </div>
            </div>
            <div className="profile-grid">
              <div className="profile-field">
                <span className="field-label"><FaUser /> {t('userParameter.username', 'Username')}</span>
                <div className="field-value">{user.username}</div>
              </div>
              <div className="profile-field">
                <span className="field-label"><FaEnvelope /> {t('userParameter.email', 'Email')}</span>
                <div className="field-value">{user.email}</div>
              </div>
              {user.shop_name && (
                <div className="profile-field">
                  <span className="field-label"><FaShoppingCart /> {t('userParameter.shopName', 'Shop Name')}</span>
                  <div className="field-value">{user.shop_name}</div>
                </div>
              )}
            </div>
          </div>

          {/* Billing */}
          {user.subscription_plan && (
            <div className="settings-card">
              <div className="card-header">
                <div className="card-title-group">
                  <h3 className="card-title"><FaCreditCard /> {t('userParameter.billing', 'Billing')}</h3>
                  <p className="card-description">{t('userParameter.billingDesc', 'Manage your subscription and payments')}</p>
                </div>
              </div>
              <div className="billing-overview">
                <div className="billing-plan">
                  <div className="plan-badge"><FaCreditCard /></div>
                  <div className="plan-details">
                    <h4>{user.subscription_plan || t('userParameter.freePlan', 'Free Plan')}</h4>
                    <p>{t('userParameter.currentPlan', 'Current Plan')}</p>
                  </div>
                </div>
                <button className="btn btn-primary" onClick={() => navigate('/billing')}>
                  {t('userParameter.manageBilling', 'Manage Billing')}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Integrations Panel */}
        <div className={`settings-panel ${activeTab === TABS.INTEGRATIONS ? 'active' : ''}`}>
          {/* Channels */}
          <div className="settings-card">
            <div className="card-header">
              <div className="card-title-group">
                <h3 className="card-title"><FaCog /> {t('userParameter.channels', 'Communication Channels')}</h3>
                <p className="card-description">{t('userParameter.channelsDesc', 'Connect your messaging and social platforms')}</p>
              </div>
            </div>
            <div className="integrations-grid">
              <IntegrationCard
                icon={<FaInstagram />}
                name="Instagram"
                iconClass="instagram"
                connected={!!user.user_insta_id}
                connectedText={user.instagram_username || 'Connected'}
                onClick={user.user_insta_id ? handleDisconnectInstagram : handleConnectInstagram}
              />
              <IntegrationCard
                icon={<FaGoogle />}
                name="Gmail"
                iconClass="google"
                connected={!!user.gmail_email_address}
                connectedText={user.gmail_email_address || 'Connected'}
                onClick={user.gmail_email_address ? handleDisconnectGoogle : handleConnectGoogle}
              />
              <IntegrationCard
                icon={<FaWhatsapp />}
                name="WhatsApp"
                iconClass="whatsapp"
                connected={!!user.whatsapp_phone_number}
                connectedText={user.whatsapp_phone_number || 'Connected'}
                onClick={user.whatsapp_phone_number ? handleDisconnectWhatsApp : () => setShowWhatsAppModal(true)}
              />
              <IntegrationCard
                icon={<SiMicrosoftoutlook />}
                name="Outlook"
                iconClass="outlook"
                connected={outlookAccounts.length > 0}
                connectedText={`${outlookAccounts.length} account${outlookAccounts.length !== 1 ? 's' : ''}`}
                onClick={handleConnectOutlook}
              />
              <IntegrationCard
                icon={<FaRobot />}
                name="Chatbot"
                iconClass="chatbot"
                connected={true}
                connectedText="Configure"
                onClick={() => navigate('/chatbot-settings')}
              />
              <IntegrationCard
                icon={<FaWarehouse />}
                name="WMS"
                iconClass="wms"
                connected={!!user.unanimity_wms_id}
                connectedText="Connected"
                onClick={handleConnectWMS}
                loading={isWmsConnecting}
              />
              <IntegrationCard
                icon={<FaPlug />}
                name={t('userParameter.connectorsCard', 'Data connectors')}
                iconClass="wms"
                connected={true}
                connectedText={t('userParameter.connectorsCardText', 'WMS, ERP, tracking APIs')}
                onClick={() => navigate('/connectors')}
              />
            </div>

            {/* Shopify Webhooks */}
            <div className="webhooks-section">
              <h4 className="webhooks-title"><FaShoppingCart /> Shopify Webhooks</h4>
              <div className="webhooks-grid">
                <button
                  className="webhook-btn register"
                  onClick={handleRegisterOrderWebhook}
                  disabled={isRegisteringWebhook || isRemovingWebhook || isRegisteringSaveOnlyWebhook}
                >
                  <FaPlus /> Register Webhook
                </button>
                <button
                  className="webhook-btn proactive"
                  onClick={handleRegisterSaveOnlyWebhook}
                  disabled={isRegisteringWebhook || isRemovingWebhook || isRegisteringSaveOnlyWebhook || !user.shop_name}
                >
                  <FaShoppingCart /> Save-Only Webhook
                </button>
                <button
                  className="webhook-btn remove"
                  onClick={handleRemoveOrderWebhook}
                  disabled={isRegisteringWebhook || isRemovingWebhook || isRegisteringSaveOnlyWebhook || !user.shop_name}
                >
                  <FaTimes /> Remove Webhooks
                </button>
              </div>
            </div>
          </div>

          {/* Connected Outlook Accounts */}
          {outlookAccounts.length > 0 && (
            <div className="settings-card">
              <div className="card-header">
                <div className="card-title-group">
                  <h3 className="card-title"><SiMicrosoftoutlook /> {t('userParameter.connectedOutlookAccounts', 'Connected Outlook Accounts')}</h3>
                  <p className="card-description">{t('userParameter.outlookAccountsDesc', 'Manage your linked Outlook email accounts')}</p>
                </div>
                <button
                  className="btn btn-secondary"
                  onClick={handleVerifySubscriptions}
                  disabled={isLoadingSubscriptions}
                >
                  <FaCheckCircle /> {isLoadingSubscriptions ? 'Verifying...' : 'Verify Subscriptions'}
                </button>
              </div>
              
              <div className="accounts-list">
                {outlookAccounts.map((account, index) => (
                  <div key={index} className="account-row">
                    <div className="account-main">
                      <div className="account-avatar">
                        <FaEnvelope />
                      </div>
                      <div className="account-details">
                        <span className="account-email">{account.outlook_email}</span>
                        <div className="account-meta">
                          <span>{t('userParameter.addedOn', 'Added')}: {new Date(account.added_at).toLocaleDateString()}</span>
                          {account.subscription_expiration && (
                            <span className="expiring">
                              {t('userParameter.expiresOn', 'Expires')}: {new Date(account.subscription_expiration).toLocaleDateString()}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="account-actions">
                      <button
                        className="account-btn remove"
                        onClick={() => handleDisconnectOutlook(account.outlook_email)}
                        title={t('userParameter.removeAccount', 'Remove account')}
                      >
                        <FaTimes />
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              {/* Subscriptions Display */}
              {showSubscriptions && subscriptions.length > 0 && (
                <div className="subscriptions-panel">
                  <div className="subscriptions-header">
                    <FaCheckCircle />
                    <h4>{t('userParameter.subscriptionStatus', 'Subscription Status')}</h4>
                  </div>
                  {subscriptions.map((account, index) => (
                    <div key={index} className="subscription-item">
                      <div className="subscription-email">
                        <FaEnvelope /> {account.email}
                      </div>
                      {account.error ? (
                        <div className="subscription-meta">
                          <span className="subscription-badge error">
                            <FaExclamationTriangle /> {account.error}
                          </span>
                        </div>
                      ) : (
                        <div className="subscription-meta">
                          <span className="subscription-badge success">
                            {account.subscriptions?.length || 0} subscriptions
                          </span>
                          {account.subscriptions?.map((sub, subIndex) => (
                            <span key={subIndex} className={`subscription-badge ${sub.matches_this_account ? 'success' : 'warning'}`}>
                              {sub.matches_this_account ? <FaCheck /> : <FaExclamationTriangle />}
                              {sub.resource}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* The account's Gmail mailbox (app password, no consent screen) */}
          {outlookAccounts.length === 0 && (() => {
            const mailbox = smtpConfig?.mailbox || null;
            const candidates = smtpConfig?.candidates || [];
            const originPage = mailbox?.origin?.kind === 'carrier_otp_mailbox'
              ? candidates.find((c) => c.carrier_id === mailbox.origin.carrier_id) : null;
            const showForm = !mailbox || smtpReplacing;
            return (
            <div className="settings-card">
              <div className="card-header">
                <div className="card-title-group">
                  <h3 className="card-title">
                    <FaGoogle /> {t('accountMailbox.title', 'Boîte Gmail du compte')}
                    {mailbox && (
                      <span className="smtp-configured-badge"><FaCheckCircle /> {t('accountMailbox.connectedBadge', 'Connectée')}</span>
                    )}
                  </h3>
                  <p className="card-description">
                    {t('accountMailbox.desc', 'La boîte d’où partent, au nom du compte, les nouveaux e-mails aux transporteurs — leurs réponses y sont lues — et les signalements de bug. Connexion par mot de passe d’application, sans écran de consentement.')}
                  </p>
                </div>
              </div>
              {smtpLoading ? (
                <div className="loading-state" style={{ padding: '24px 0' }}>
                  <div className="loading-spinner"></div>
                  <span>{t('common.loading', 'Loading...')}</span>
                </div>
              ) : (
                <div className="api-fields">
                  {mailbox && (
                    <div className="account-mailbox-current">
                      <div className="account-mailbox-address"><FaEnvelope /> {mailbox.email}</div>
                      <small className="smtp-help-text">
                        <FaInfoCircle />{' '}
                        {originPage
                          ? t('accountMailbox.fromOtp', 'Reprise de la boîte de double authentification de la fiche {{carrier}}.', { carrier: originPage.carrier_name })
                          : mailbox.origin?.kind === 'carrier_otp_mailbox'
                            ? t('accountMailbox.fromOtpGone', 'Reprise d’une boîte de double authentification.')
                            : t('accountMailbox.manual', 'Connectée avec son mot de passe d’application.')}
                        {' '}{t('accountMailbox.threadsNote', 'Les fils transporteur ouverts avant sa connexion restent sur la boîte de la plateforme.')}
                      </small>
                      {mailbox.problem && (
                        <div className="account-mailbox-problem"><FaExclamationTriangle /> {mailbox.problem}</div>
                      )}
                      <div className="api-actions">
                        {!smtpReplacing && (
                          <button className="btn btn-secondary" onClick={() => setSmtpReplacing(true)} disabled={smtpSaving}>
                            <FaEdit /> {t('accountMailbox.replace', 'Changer de boîte')}
                          </button>
                        )}
                        <button className="btn btn-danger" onClick={handleRemoveSmtpConfig} disabled={smtpSaving}>
                          <FaTrashAlt /> {t('accountMailbox.disconnect', 'Déconnecter')}
                        </button>
                      </div>
                    </div>
                  )}
                  {showForm && candidates.length > 0 && (
                    <div className="account-mailbox-candidates">
                      {candidates.filter((c) => c.email !== mailbox?.email).map((c) => (
                        <div className="account-mailbox-candidate" key={c.carrier_id}>
                          <div>
                            <div className="account-mailbox-address"><FaEnvelope /> {c.email}</div>
                            <small className="smtp-help-text">
                              {t('accountMailbox.candidateHint', 'Boîte de double authentification de la fiche {{carrier}} : son adresse et son mot de passe d’application sont repris.', { carrier: c.carrier_name })}
                            </small>
                          </div>
                          <button className="btn btn-primary" onClick={() => handleUseOtpMailbox(c.carrier_id)} disabled={smtpSaving}>
                            <FaCheckCircle /> {smtpSaving ? t('common.saving', 'Saving...') : t('accountMailbox.useOtp', 'Utiliser cette boîte')}
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  {showForm && (
                  <>
                  <div className="api-field-group">
                    <label className="api-field-label"><FaEnvelope /> {t('userParameter.smtpEmail', 'Gmail Address')}</label>
                    <div className="api-input-row">
                      <input
                        type="email"
                        className="api-input"
                        value={smtpForm.email}
                        onChange={(e) => setSmtpForm((f) => ({ ...f, email: e.target.value }))}
                        placeholder="user@gmail.com"
                        disabled={smtpSaving}
                      />
                    </div>
                  </div>
                  <div className="api-field-group">
                    <label className="api-field-label"><FaKey /> {t('userParameter.smtpPassword', 'App Password')}</label>
                    <div className="api-input-row">
                      <input
                        type={showSmtpPassword ? 'text' : 'password'}
                        className="api-input"
                        value={smtpForm.password}
                        onChange={(e) => setSmtpForm((f) => ({ ...f, password: e.target.value }))}
                        placeholder="abcd-efgh-ijkl-mnop"
                        disabled={smtpSaving}
                      />
                      <button
                        type="button"
                        className="icon-btn"
                        onClick={() => setShowSmtpPassword(!showSmtpPassword)}
                        title={showSmtpPassword ? 'Hide' : 'Show'}
                      >
                        {showSmtpPassword ? <FaEyeSlash /> : <FaEye />}
                      </button>
                    </div>
                    <small className="smtp-help-text">
                      <FaInfoCircle /> {t('userParameter.smtpPasswordHelp', 'Use a Gmail App Password, not your regular password.')}{' '}
                      <a href="https://support.google.com/accounts/answer/185833" target="_blank" rel="noopener noreferrer">
                        {t('userParameter.smtpPasswordHelpLink', 'Learn how to create one')}
                      </a>
                    </small>
                  </div>
                  <div className="api-field-group">
                    <label className="api-field-label"><FaServer /> {t('userParameter.smtpServer', 'SMTP Server')}</label>
                    <div className="api-input-row">
                      <input
                        type="text"
                        className="api-input"
                        value={smtpForm.smtp_server}
                        onChange={(e) => setSmtpForm((f) => ({ ...f, smtp_server: e.target.value }))}
                        placeholder="smtp.gmail.com"
                        disabled={smtpSaving}
                      />
                    </div>
                  </div>
                  <div className="api-field-group">
                    <label className="api-field-label"><FaHashtag /> {t('userParameter.smtpPort', 'SMTP Port')}</label>
                    <div className="api-input-row">
                      <input
                        type="number"
                        className="api-input"
                        value={smtpForm.smtp_port}
                        onChange={(e) => setSmtpForm((f) => ({ ...f, smtp_port: e.target.value }))}
                        placeholder="587"
                        disabled={smtpSaving}
                      />
                    </div>
                  </div>
                  <div className="api-actions">
                    <button
                      className="btn btn-primary"
                      onClick={handleSaveSmtpConfig}
                      disabled={smtpSaving || !smtpForm.email || !smtpForm.password}
                    >
                      <FaSave /> {smtpSaving ? t('common.saving', 'Saving...') : t('accountMailbox.connect', 'Connecter')}
                    </button>
                    {mailbox && smtpReplacing && (
                      <button className="btn btn-secondary" onClick={() => setSmtpReplacing(false)} disabled={smtpSaving}>
                        {t('common.cancel', 'Cancel')}
                      </button>
                    )}
                  </div>
                  </>
                  )}
                </div>
              )}
            </div>
            );
          })()}

          {/* Spacefill Integration */}
          <div className="settings-card">
            <div className="card-header">
              <div className="card-title-group">
                <h3 className="card-title">
                  <FaWarehouse /> {t('userParameter.spacefillTitle', 'Spacefill')}
                  {spacefillConfigured && spacefillEnabled && (
                    <span className="smtp-configured-badge"><FaCheckCircle /> {t('userParameter.spacefillConnected', 'Connected')}</span>
                  )}
                </h3>
                <p className="card-description">
                  {t('userParameter.spacefillDesc', 'Receive the incidents your clients open on Spacefill as tickets (with attachments), and reply to them directly from Unanimity. Incidents are synced every 5 minutes.')}
                </p>
              </div>
            </div>
            <div className="api-fields">
              <div className="api-field-group">
                <label className="api-field-label"><FaKey /> {t('userParameter.spacefillToken', 'Spacefill API Token')}</label>
                <div className="api-input-row">
                  <input
                    type={showSpacefillToken ? 'text' : 'password'}
                    className="api-input"
                    value={spacefillTokenInput}
                    onChange={(e) => setSpacefillTokenInput(e.target.value)}
                    placeholder={spacefillTokenSet
                      ? t('userParameter.spacefillTokenPlaceholderSet', 'Token saved — leave blank to keep it')
                      : t('userParameter.spacefillTokenPlaceholder', 'Paste your Spacefill API token')}
                    disabled={isLoadingSpacefill || isSavingSpacefill}
                  />
                  <button
                    type="button"
                    className="icon-btn"
                    onClick={() => setShowSpacefillToken(!showSpacefillToken)}
                    title={showSpacefillToken ? 'Hide token' : 'Show token'}
                  >
                    {showSpacefillToken ? <FaEyeSlash /> : <FaEye />}
                  </button>
                </div>
                <small className="smtp-help-text">
                  <FaInfoCircle /> {t('userParameter.spacefillTokenHelp', 'Generate an API token in your Spacefill account (logistic provider access), then paste it here.')}
                </small>
              </div>
              <div className="api-field-group">
                <label className="api-field-label"><FaFilter /> {t('userParameter.spacefillIncidentTypes', 'Incident types to sync')}</label>
                <input
                  type="text"
                  className="api-input"
                  value={spacefillIncidentTypesInput}
                  onChange={(e) => setSpacefillIncidentTypesInput(e.target.value)}
                  placeholder={t('userParameter.spacefillIncidentTypesPlaceholder', 'e.g. Transport — leave blank to sync all types')}
                  disabled={isLoadingSpacefill || isSavingSpacefill}
                />
                <small className="smtp-help-text">
                  <FaInfoCircle /> {t('userParameter.spacefillIncidentTypesHelp', 'Comma-separated list of Spacefill incident types to import as tickets. Other types are ignored; incidents without a type are always imported. Leave blank to import everything.')}
                </small>
              </div>
              <div className="api-field-group">
                <label className="api-field-label" style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={spacefillEnabled}
                    onChange={(e) => setSpacefillEnabled(e.target.checked)}
                    disabled={isLoadingSpacefill || isSavingSpacefill}
                  />
                  {t('userParameter.spacefillEnabled', 'Enable incident synchronization')}
                </label>
              </div>
              {spacefillConfigured && spacefillSyncState && (
                <small className="smtp-help-text">
                  {spacefillSyncState.last_run_at && (
                    <span>
                      {t('userParameter.spacefillLastSync', 'Last sync')}: {new Date(spacefillSyncState.last_run_at).toLocaleString()}
                    </span>
                  )}
                  {spacefillSyncState.last_error && (
                    <span style={{ color: '#e53e3e', marginLeft: '8px' }}>
                      <FaExclamationTriangle /> {spacefillSyncState.last_error}
                    </span>
                  )}
                </small>
              )}
              <div className="api-actions">
                <button
                  className="btn btn-primary"
                  onClick={handleSaveSpacefillConfig}
                  disabled={isSavingSpacefill || isLoadingSpacefill}
                >
                  <FaSave /> {isSavingSpacefill ? t('common.saving', 'Saving...') : t('userParameter.save', 'Save')}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Notifications Panel */}
        <div className={`settings-panel ${activeTab === TABS.NOTIFICATIONS ? 'active' : ''}`}>
          {/* Slack Integration */}
          <div className="settings-card">
            <div className="card-header">
              <div className="card-title-group">
                <h3 className="card-title"><FaSlack /> {t('userParameter.slackIntegration', 'Slack Integration')}</h3>
                <p className="card-description">{t('userParameter.slackDesc', 'Receive ticket notifications in your Slack workspace')}</p>
              </div>
            </div>
            <div className="api-fields">
              <div className="api-field-group">
                <label className="api-field-label"><FaKey /> {t('userParameter.slackToken', 'Bot Token')}</label>
                <div className="api-input-row">
                  <input
                    type={showSlackToken ? 'text' : 'password'}
                    className="api-input"
                    value={slackToken}
                    onChange={(e) => setSlackToken(e.target.value)}
                    placeholder={t('userParameter.enterSlackToken', 'xoxb-your-token-here')}
                    disabled={isLoadingSlackCredentials}
                  />
                  <button
                    type="button"
                    className="icon-btn"
                    onClick={() => setShowSlackToken(!showSlackToken)}
                    title={showSlackToken ? 'Hide token' : 'Show token'}
                  >
                    {showSlackToken ? <FaEyeSlash /> : <FaEye />}
                  </button>
                </div>
              </div>
              <div className="api-field-group">
                <label className="api-field-label"><FaHashtag /> {t('userParameter.slackChannelId', 'Channel ID')}</label>
                <div className="api-input-row">
                  <input
                    type="text"
                    className="api-input"
                    value={slackChannelId}
                    onChange={(e) => setSlackChannelId(e.target.value)}
                    placeholder={t('userParameter.enterSlackChannelId', 'C01234567')}
                    disabled={isLoadingSlackCredentials}
                  />
                </div>
              </div>
              <div className="api-actions">
                <button
                  className="btn btn-primary"
                  onClick={handleSaveSlackCredentials}
                  disabled={isSavingSlackCredentials || isLoadingSlackCredentials}
                >
                  <FaSave /> {isSavingSlackCredentials ? 'Saving...' : t('userParameter.save', 'Save')}
                </button>
              </div>
            </div>
          </div>

          {/* Blocked Emails */}
          <div className="settings-card">
            <div className="card-header">
              <div className="card-title-group">
                <h3 className="card-title"><FaBan /> {t('userParameter.blockedEmails', 'Blocked Emails')}</h3>
                <p className="card-description">{t('userParameter.blockedEmailsDescription', 'Emails from these addresses will not create tickets')}</p>
              </div>
            </div>
            <div className="api-fields">
              <div className="blocked-senders__add">
                <input
                  type="text"
                  className="blocked-senders__input"
                  value={newBlockedEntry}
                  onChange={(e) => { setNewBlockedEntry(e.target.value); setBlockedEntryError(''); }}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddBlockedEntry(); } }}
                  placeholder={t('userParameter.blockedSendersPlaceholder', 'name@example.com or example.com')}
                  disabled={isLoadingBlockedEmails || isSavingBlockedEmails}
                />
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleAddBlockedEntry}
                  disabled={isSavingBlockedEmails || isLoadingBlockedEmails || !newBlockedEntry.trim()}
                >
                  <FaPlus /> {t('userParameter.blockedSendersAdd', 'Add')}
                </button>
              </div>
              {blockedEntryError && <p className="blocked-senders__error">{blockedEntryError}</p>}
              {blockedEntries.length === 0 && !isLoadingBlockedEmails && (
                <p className="card-description">{t('userParameter.blockedSendersEmpty', 'No ignored address or domain yet.')}</p>
              )}
              <ul className="blocked-senders__list">
                {blockedEntries.map((entry) => (
                  <li key={entry.value} className={`blocked-senders__chip blocked-senders__chip--${entry.kind}`}>
                    <span className="blocked-senders__kind">
                      {entry.kind === 'domain'
                        ? t('userParameter.blockedSendersKindDomain', 'domain')
                        : t('userParameter.blockedSendersKindEmail', 'address')}
                    </span>
                    <span className="blocked-senders__value">{entry.value}</span>
                    <button
                      type="button"
                      className="blocked-senders__remove"
                      onClick={() => handleRemoveBlockedEntry(entry.value)}
                      disabled={isSavingBlockedEmails}
                      aria-label={t('userParameter.blockedSendersRemove', 'Remove')}
                      title={t('userParameter.blockedSendersRemove', 'Remove')}
                    >
                      <FaTimes />
                    </button>
                  </li>
                ))}
              </ul>
              {blockedInvalid.length > 0 && (
                <p className="blocked-senders__error">
                  {t('userParameter.blockedSendersInvalidStored', 'Ignored because unreadable (fix or remove them): {{entries}}', { entries: blockedInvalid.join(', ') })}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Security Panel */}
        <div className={`settings-panel ${activeTab === TABS.SECURITY ? 'active' : ''}`}>
          {/* Password */}
          <div className="settings-card">
            <div className="card-header">
              <div className="card-title-group">
                <h3 className="card-title"><FaLock /> {t('userParameter.password', 'Password')}</h3>
                <p className="card-description">{t('userParameter.passwordDesc', 'Update your account password')}</p>
              </div>
              <button className="btn btn-secondary" onClick={() => setShowPasswordModal(true)}>
                <FaLock /> {t('userParameter.changePassword', 'Change Password')}
              </button>
            </div>
          </div>

          {/* OTP Code */}
          {user.fa_account && (
            <div className="settings-card">
              <div className="card-header">
                <div className="card-title-group">
                  <h3 className="card-title"><FaKey /> {t('userParameter.otpCode', 'OTP Code')}</h3>
                  <p className="card-description">{t('userParameter.otpCodeDesc', 'Update the OTP code for your account')}</p>
                </div>
                <button className="btn btn-secondary" onClick={handleOpenOtpModal}>
                  <FaKey /> {t('userParameter.changeOtp', 'Change OTP')}
                </button>
              </div>
            </div>
          )}

          {/* Danger Zone */}
          <div className="settings-card danger">
            <div className="card-header">
              <div className="card-title-group">
                <h3 className="card-title" style={{ color: 'var(--state-danger)' }}>
                  <FaExclamationTriangle /> {t('userParameter.dangerZone', 'Danger Zone')}
                </h3>
                <p className="card-description">{t('userParameter.dangerZoneDesc', 'Irreversible actions that affect your account')}</p>
              </div>
            </div>
            <button className="btn btn-danger" onClick={() => setShowDeleteModal(true)}>
              <FaTrash /> {t('userParameter.deleteAccount', 'Delete Account')}
            </button>
          </div>
        </div>
      </div>

      {/* Password Modal */}
      {showPasswordModal && (
        <Modal
          title={t('userParameter.changePassword', 'Change Password')}
          onClose={() => setShowPasswordModal(false)}
        >
          <form onSubmit={handlePasswordSubmit}>
            <div className="form-group">
              <label className="form-label">{t('userParameter.currentPassword', 'Current Password')}</label>
              <input
                type="password"
                className="form-input"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                required
              />
            </div>
            <div className="form-group">
              <label className="form-label">{t('userParameter.newPassword', 'New Password')}</label>
              <input
                type="password"
                className="form-input"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
              />
            </div>
            <div className="form-group">
              <label className="form-label">{t('userParameter.confirmNewPassword', 'Confirm New Password')}</label>
              <input
                type="password"
                className="form-input"
                value={confirmNewPassword}
                onChange={(e) => setConfirmNewPassword(e.target.value)}
                required
              />
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={() => setShowPasswordModal(false)}>
                {t('common.cancel', 'Cancel')}
              </button>
              <button type="submit" className="btn btn-primary">
                {t('userParameter.updatePassword', 'Update Password')}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* Delete Account Modal */}
      {showDeleteModal && (
        <Modal
          title={t('userParameter.deleteAccount', 'Delete Account')}
          onClose={() => setShowDeleteModal(false)}
        >
          <form onSubmit={handleDeleteUser}>
            <div className="form-warning">
              <FaExclamationTriangle />
              <p>{t('userParameter.deleteAccountWarning', 'This action cannot be undone. All your data will be permanently deleted.')}</p>
            </div>
            <div className="form-group">
              <label className="form-label">
                {t('userParameter.confirmUsernameLabel', 'Type your username to confirm:')} <strong>{user.username}</strong>
              </label>
              <input
                type="text"
                className="form-input"
                value={confirmUsername}
                onChange={(e) => setConfirmUsername(e.target.value)}
                required
              />
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={() => setShowDeleteModal(false)}>
                {t('common.cancel', 'Cancel')}
              </button>
              <button type="submit" className="btn btn-danger-solid">
                <FaTrash /> {t('userParameter.confirmDelete', 'Delete Account')}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* WhatsApp Modal */}
      {showWhatsAppModal && (
        <Modal
          title={t('userParameter.whatsAppIntegration', 'WhatsApp Integration')}
          onClose={() => setShowWhatsAppModal(false)}
        >
          <WhatsAppIntegration />
        </Modal>
      )}

      {/* Chatbot Modal */}
      {showChatbotModal && (
        <Modal
          title={t('userParameter.chatbotSettings', 'Chatbot Settings')}
          onClose={() => setShowChatbotModal(false)}
        >
          <ChatbotSettings />
        </Modal>
      )}

      {/* OTP Change Modal */}
      {showOtpModal && (
        <Modal
          title={t('userParameter.changeOtpTitle', 'Change OTP Code')}
          onClose={handleCloseOtpModal}
        >
          <form onSubmit={handleSubmitOtpChange}>
            <div className="form-group">
              <label className="form-label">{t('userParameter.newOtpCode', 'New OTP Code')}</label>
              <input
                type="text"
                className="form-input"
                value={newOtpCode}
                onChange={(e) => setNewOtpCode(e.target.value)}
                placeholder={t('userParameter.enterNewOtpCode', 'Enter your new OTP code')}
                required
              />
              <small style={{ display: 'block', marginTop: '6px', fontSize: '12px', color: 'var(--text-secondary)', fontStyle: 'italic' }}>
                {t('userParameter.otpCodeHelp', 'Enter the new OTP code from your authenticator app')}
              </small>
            </div>
            {submitOtpError && (
              <div className="form-warning">
                <FaExclamationTriangle />
                <p>{submitOtpError}</p>
              </div>
            )}
            {submitOtpSuccess && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '12px', background: 'rgba(16, 185, 129, 0.1)', borderRadius: '8px', color: '#10b981', marginBottom: '16px' }}>
                <FaCheckCircle />
                <p style={{ margin: 0 }}>{submitOtpSuccess}</p>
              </div>
            )}
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={handleCloseOtpModal}>
                {t('common.cancel', 'Cancel')}
              </button>
              <button type="submit" className="btn btn-primary" disabled={isSubmittingOtp}>
                {isSubmittingOtp
                  ? t('userParameter.updatingOtp', 'Updating...')
                  : t('userParameter.updateOtp', 'Update OTP')}
              </button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
};

// ============================================
// INTEGRATION CARD COMPONENT
// ============================================
const IntegrationCard = ({ icon, name, iconClass, connected, connectedText, onClick, loading }) => {
  const { t } = useTranslation();
  return (
    <div 
      className={`integration-card ${connected ? 'connected' : ''}`}
      onClick={!loading ? onClick : undefined}
      role="button"
      tabIndex={0}
      onKeyPress={(e) => e.key === 'Enter' && !loading && onClick()}
    >
      <div className={`integration-icon ${iconClass}`}>
        {icon}
      </div>
      <span className="integration-name">{name}</span>
      <span className={`integration-status ${connected ? 'connected' : ''}`}>
        <span className="status-dot"></span>
        {loading ? t('userParameter.connecting', 'Connexion…') : connected ? connectedText : t('userParameter.notConnected', 'Non connecté')}
      </span>
    </div>
  );
};

// ============================================
// MODAL COMPONENT
// ============================================
const Modal = ({ children, onClose, title }) => {
  return (
    <div className="modal-backdrop" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal-container">
        <div className="modal-header">
          <h3 className="modal-title">{title}</h3>
          <button className="modal-close" onClick={onClose}>
            <FaTimes />
          </button>
        </div>
        <div className="modal-body">
          {children}
        </div>
      </div>
    </div>
  );
};

export default UserParameter;
